import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Search,
  Plus,
  Download,
  Trash2,
  Edit,
  Copy,
  MoreHorizontal,
  CalendarIcon,
  TrendingUp,
  Users,
  DollarSign,
  Percent,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAdminAuth } from "@/context/Admin";
import { toast } from "sonner";
import Loader from "@/components/loader/loader";

export default function CouponManagement() {
  const {
    createCoupon,
    updateCoupon,
    deleteCoupon,
    listCoupons,
    getCouponInfo,
  } = useAdminAuth();

  // UI state
  const [coupons, setCoupons] = useState([]);
  const [Loading, setLoading] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal + form
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Controlled form state
  const initialForm = {
    code: "",
    type: "percentage",
    value: "",
    usageLimit: "",
    description: "",
    minOrderAmount: "",
    eligibleProducts: "",
    active: true,
  };
  const [form, setForm] = useState(initialForm);

  // Help info from server
  const [couponInfo, setCouponInfo] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);

  // fetch coupons from server
  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.type = typeFilter;
      if (searchTerm) params.code = searchTerm;
      // optional: page/limit
      params.page = currentPage;
      params.limit = itemsPerPage;

      const data = await listCoupons(params);
      // Normalize coupon data structure (backend may return discountType or discount_type)
      if (Array.isArray(data)) {
        const normalizedCoupons = data.map((coupon) => ({
          ...coupon,
          discount_type: coupon.discountType || coupon.discount_type,
          discount_value: coupon.discountValue ?? coupon.discount_value,
          usage_count: coupon.usageCount ?? coupon.usage_count ?? 0,
          usage_limit: coupon.usageLimit ?? coupon.usage_limit,
          expiration_date: coupon.expirationDate || coupon.expiration_date,
          created_at: coupon.createdAt || coupon.created_at,
          min_order_amount: coupon.minOrderAmount ?? coupon.min_order_amount,
          eligible_products: coupon.eligibleProducts || coupon.eligible_products,
          status: coupon.status || (coupon.expiration_date && new Date(coupon.expiration_date) < new Date() ? "expired" : "active"),
        }));
        setCoupons(normalizedCoupons);
      } else {
        setCoupons([]);
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, searchTerm, currentPage]);

  // When opening help, fetch coupon info from server
  useEffect(() => {
    if (!isHelpOpen) return;
    (async () => {
      setHelpLoading(true);
      try {
        const info = await getCouponInfo();
        setCouponInfo(info);
      } catch (err) {
        console.error("Failed to fetch coupon info", err);
        setCouponInfo(null);
      } finally {
        setHelpLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHelpOpen]);

  const handleSelectAll = (checked) => {
    if (checked) setSelectedCoupons(coupons.map((c) => c.id));
    else setSelectedCoupons([]);
  };

  const handleSelectCoupon = (couponId, checked) => {
    if (checked) setSelectedCoupons((s) => [...s, couponId]);
    else setSelectedCoupons((s) => s.filter((id) => id !== couponId));
  };

  const resetForm = () => {
    setForm(initialForm);
    setStartDate(null);
    setEndDate(null);
  };

  const handleCreateCoupon = () => {
    setEditingCoupon(null);
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    // map server coupon fields to form
    setForm({
      code: coupon.code || "",
      type: coupon.discount_type || "percentage",
      value: coupon.discount_value ?? "",
      usageLimit: coupon.usage_limit ?? "",
      description: coupon.description || "",
      minOrderAmount: coupon.min_order_amount ?? "",
      eligibleProducts: coupon.eligible_products ?? "",
      active: !(coupon.expiration_date && new Date(coupon.expiration_date) < new Date()),
    });

    setStartDate(null);
    setEndDate(coupon.expiration_date ? new Date(coupon.expiration_date) : null);
    setIsCreateModalOpen(true);
  };

  const handleSubmitForm = async (e) => {
    e?.preventDefault?.();

    const payload = {
      code: form.code,
      discount_type: form.type,
      discount_value: Number(form.value) || 0,
      min_order_amount: form.minOrderAmount ? Number(form.minOrderAmount) : 0,
      eligible_products: form.eligibleProducts || null,
      expiration_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
      usage_limit: form.usageLimit ? Number(form.usageLimit) : 1,
    };

    try {
      if (editingCoupon) {
        await updateCoupon(editingCoupon.id, payload);
        // toast.success("Coupon updated");
      } else {
        await createCoupon(payload);
        // toast.success("Coupon created");
      }

      setIsCreateModalOpen(false);
      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to save coupon");
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this coupon?");
    if (!ok) return;

    try {
      await deleteCoupon(id);
      toast.success("Deleted");
      setSelectedCoupons((s) => s.filter((x) => x !== id));
      fetchCoupons();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete coupon");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCoupons.length === 0) return;
    const ok = window.confirm(`Delete ${selectedCoupons.length} selected coupon(s)?`);
    if (!ok) return;

    try {
      // note: backend doesn't provide bulk delete — we do individual deletes in parallel
      await Promise.all(selectedCoupons.map((id) => deleteCoupon(id)));
    //   toast.success("Selected coupons deleted");
      setSelectedCoupons([]);
      fetchCoupons();
    } catch (err) {
      console.error(err);
    //   toast.error("Failed to delete selected coupons");
    }
  };

  const handleDuplicate = async (coupon) => {
    const code = `${coupon.code}_COPY_${Date.now().toString().slice(-4)}`;
    const payload = {
      code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_order_amount: coupon.min_order_amount || 0,
      eligible_products: coupon.eligible_products || null,
      expiration_date: coupon.expiration_date || null,
      usage_limit: coupon.usage_limit ?? 1,
    };

    try {
      await createCoupon(payload);
    //   toast.success("Coupon duplicated");
      fetchCoupons();
    } catch (err) {
      console.error(err);
    //   toast.error("Failed to duplicate coupon");
    }
  };

  const exportSelected = () => {
    if (selectedCoupons.length === 0) {
      toast.error("No coupons selected to export");
      return;
    }

    const rows = coupons.filter((c) => selectedCoupons.includes(c.id));
    const csv = [
      ["id", "code", "type", "value", "usage_count", "usage_limit", "expiration_date"].join(","),
      ...rows.map((r) =>
        [r.id, r.code, r.discount_type, r.discount_value, r.usage_count ?? "", r.usage_limit ?? "", r.expiration_date ?? ""].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "coupons_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // pagination helpers (client-side)
  const totalPages = Math.max(1, Math.ceil(coupons.length / itemsPerPage));
  const paginated = coupons.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (Loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Coupon Management</h1>
          <p className="text-muted-foreground">Create and manage discount coupons</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsHelpOpen(true)}>
            <HelpCircle className="h-4 w-4 mr-2" />
            Help
          </Button>
          <Button onClick={handleCreateCoupon}>
            <Plus className="h-4 w-4 mr-2" />
            Create Coupon
          </Button>
        </div>
      </div>

      {/* Summary Stats (simple static placeholders) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Coupons</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{coupons.length}</div>
            <p className="text-xs text-muted-foreground">+2 from last month</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Coupons</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(coupons || []).filter(c => !(c.expiration_date && new Date(c.expiration_date) < new Date())).length}</div>
            <p className="text-xs text-muted-foreground">% of total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(coupons || []).reduce((acc, c) => acc + (Number(c.usage_count) || 0), 0)}</div>
            <p className="text-xs text-muted-foreground">+12% from last month</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Savings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₦12,450</div>
            <p className="text-xs text-muted-foreground">Customer savings</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search coupons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="percentage">Percentage</SelectItem>
                <SelectItem value="fixed">Fixed Amount</SelectItem>
                <SelectItem value="free_shipping">Free Shipping</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedCoupons.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{selectedCoupons.length} coupon(s) selected</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportSelected}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coupons Table */}
      <Card>
        <CardContent className="p-0">
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left p-4">
                    <Checkbox
                      checked={selectedCoupons.length === coupons.length && coupons.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="text-left p-4 font-medium">Code</th>
                  <th className="text-left p-4 font-medium">Type</th>
                  <th className="text-left p-4 font-medium">Value</th>
                  <th className="text-left p-4 font-medium">Usage</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Valid Until</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((coupon) => (
                  <tr key={coupon.id} className="border-b hover:bg-muted/50">
                    <td className="p-4">
                      <Checkbox
                        checked={selectedCoupons.includes(coupon.id)}
                        onCheckedChange={(checked) => handleSelectCoupon(coupon.id, checked)}
                      />
                    </td>
                    <td className="p-4">
                      <div>
                        <div className="font-medium">{coupon.code}</div>
                        <div className="text-sm text-muted-foreground truncate max-w-[200px]">{coupon.description}</div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">
                        {coupon.discount_type === "percentage"
                          ? "Percentage"
                          : coupon.discount_type === "fixed"
                          ? "Fixed"
                          : "Free Shipping"}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {coupon.discount_type === "percentage"
                        ? `${coupon.discount_value}%`
                        : coupon.discount_type === "fixed"
                        ? `₦${coupon.discount_value}`
                        : "Free"}
                    </td>
                    <td className="p-4">
                      <div className="text-sm">
                        {coupon.usage_count ?? 0} / {coupon.usage_limit ?? "∞"}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant={coupon.status === "active" ? "default" : "secondary"}>{coupon.status ?? (coupon.expiration_date && new Date(coupon.expiration_date) < new Date() ? 'expired' : 'active')}</Badge>
                    </td>
                    <td className="p-4 text-sm">{coupon.expiration_date ?? "—"}</td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditCoupon(coupon)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(coupon)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(coupon.id)}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 p-4">
            {paginated.map((coupon) => (
              <Card key={coupon.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={selectedCoupons.includes(coupon.id)}
                        onCheckedChange={(checked) => handleSelectCoupon(coupon.id, checked)}
                      />
                      <div>
                        <div className="font-medium">{coupon.code}</div>
                        <Badge variant={coupon.status === "active" ? "default" : "secondary"} className="mt-1">
                          {coupon.status}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditCoupon(coupon)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(coupon)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(coupon.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <Badge variant="outline">
                        {coupon.discount_type === "percentage"
                          ? "Percentage"
                          : coupon.discount_type === "fixed"
                          ? "Fixed"
                          : "Free Shipping"}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Value:</span>
                      <span>
                        {coupon.discount_type === "percentage"
                          ? `${coupon.discount_value}%`
                          : coupon.discount_type === "fixed"
                          ? `$${coupon.discount_value}`
                          : "Free"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Usage:</span>
                      <span>
                        {coupon.usage_count ?? 0} / {coupon.usage_limit ?? "∞"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valid Until:</span>
                      <span>{coupon.expiration_date ?? "—"}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground">{coupon.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, coupons.length)} of {coupons.length} coupons
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button key={page} variant={currentPage === page ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(page)} className="w-8 h-8 p-0">
                {page}
              </Button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? "Edit Coupon" : "Create New Coupon"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitForm} className="space-y-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Coupon Code</Label>
                <Input id="code" placeholder="Enter coupon code" value={form.code} onChange={(e) => setForm(f => ({...f, code: e.target.value}))} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Discount Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({...f, type: v}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="free_shipping">Free Shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="value">Discount Value</Label>
                <Input id="value" type="number" placeholder="Enter value" value={form.value} onChange={(e) => setForm(f => ({...f, value: e.target.value}))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usageLimit">Usage Limit</Label>
                <Input id="usageLimit" type="number" placeholder="Leave empty for unlimited" value={form.usageLimit} onChange={(e) => setForm(f => ({...f, usageLimit: e.target.value}))} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" placeholder="Enter coupon description" value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal bg-transparent">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal bg-transparent">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch id="active" checked={form.active} onCheckedChange={(checked) => setForm(f => ({...f, active: checked}))} />
              <Label htmlFor="active">Active</Label>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" type="button" onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit">{editingCoupon ? "Update Coupon" : "Create Coupon"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Help Guide Modal */}
      <Dialog open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Coupon Management Help</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="types">Coupon Types</TabsTrigger>
              <TabsTrigger value="best-practices">Best Practices</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Getting Started</h3>
                <p className="text-sm text-muted-foreground">Use this dashboard to create, manage, and track your discount coupons. You can create different types of discounts, set usage limits, and monitor performance.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Server-side Info</h3>
                {helpLoading ? (
                  <p>Loading...</p>
                ) : couponInfo ? (
                  <pre className="text-sm whitespace-pre-wrap">{JSON.stringify(couponInfo, null, 2)}</pre>
                ) : (
                  <p className="text-sm text-muted-foreground">No server guidance available.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="types" className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Percentage Discount</h3>
                <p className="text-sm text-muted-foreground">Offers a percentage off the total order value (e.g., 25% off)</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Fixed Amount</h3>
                <p className="text-sm text-muted-foreground">Provides a fixed dollar amount off the order (e.g., $10 off)</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Free Shipping</h3>
                <p className="text-sm text-muted-foreground">Waives shipping costs for qualifying orders</p>
              </div>
            </TabsContent>

            <TabsContent value="best-practices" className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Coupon Code Guidelines</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Use clear, memorable codes (e.g., SUMMER25, NEWUSER10)</li>
                  <li>• Avoid confusing characters (0, O, I, l)</li>
                  <li>• Keep codes between 6-12 characters</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Usage Limits</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Set reasonable usage limits to control costs</li>
                  <li>• Consider one-time use for high-value discounts</li>
                  <li>• Monitor usage regularly and adjust as needed</li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
